import { useEffect, useState, type ReactElement } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { SimplePagination } from '@/components/shared/SimplePagination';
import { Loading } from '@/components/shared/placeholders/Loading';
import { ErrorFallback } from '@/components/shared/placeholders/Error';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { ScanDetailFormattedText } from '@/components/ScanDetailFormattedText';
import {
  hasMcpDetectionEvidence,
  trimMcpToolLocationPreview,
} from './alert-location';
import {
  ClientTypeCell,
  ClientTypesCell,
  OutcomeBadge,
  OccurrenceOutcomeCell,
} from '@/components/DataTable/alerts/CellRenderers';
import {
  useUserAlertOccurrences,
  useOrganizationAlertOccurrences,
} from '@/hooks/api/useGroupedAlerts';
import { getUserDisplayName } from '@/lib/utils';
import { UserTypeTag } from './UserTypeTag';
import {
  formatAlertType,
  formatSubPolicyLabel,
} from '@kirin/api-client/helpers/alerts';
import { getAlertFileDisplayLabel } from '@kirin/api-client/contracts';
import { MiddleTruncatedPath } from '@/components/shared/MiddleTruncatedPath';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import {
  ORGANIZATION_SCOPE_LABEL,
  securityGroupLabel,
} from './security-groups';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import type { AlertOccurrencesResponse } from '@kirin/api-client/contracts/alerts';
import type { FilterValue } from '@kirin/api-client/contracts/table';
import { ALERTS_PAGE_SIZE } from './constants';
import { AlertDetailCard } from './AlertDetailCard';
import { CapabilitySummary } from './CapabilitySummary';
import { UsersCell } from './UsersCell';
import { useAlertsIdeColumnEnabled } from '@/hooks/useAlertsIdeColumnEnabled';
import { EndpointDetailsPopover } from '@/components/Inventory/EndpointDetailsPopover';

interface AlertOccurrencesPanelProps {
  alertKey: string;
  organizationId?: string;
  /** Column filters from the main alerts table to apply to occurrences. */
  filters?: FilterValue[];
  /** When "modal", table scrolls and pagination is fixed at bottom. */
  layout?: 'default' | 'modal';
  /** Called when data has loaded (e.g. for modal title). */
  onDataLoaded?: (data: AlertOccurrencesResponse) => void;
}

/**
 * Renders paginated occurrences for a grouped alert.
 * Uses user-level API when organizationId is omitted, org-level when provided.
 * Uses shared ui/table, SimplePagination, Loading, and ErrorFallback.
 */
export function AlertOccurrencesPanel({
  alertKey,
  organizationId,
  filters,
  layout = 'default',
  onDataLoaded,
}: AlertOccurrencesPanelProps): ReactElement | null {
  const [page, setPage] = useState(1);
  const params = {
    page,
    limit: ALERTS_PAGE_SIZE,
    ...(filters && filters.length > 0 ? { filters } : {}),
  };

  const ideColumnEnabled = useAlertsIdeColumnEnabled();

  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ALERTS_REVAMP,
    flagsmithFeatureFlags.CAPABILITY_DETECTION,
    flagsmithFeatureFlags.ENDPOINT_IDENTIFIER,
    flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES,
  ]);
  const revampEnabled = flags[flagsmithFeatureFlags.ALERTS_REVAMP] === true;
  const capabilityDetectionEnabled =
    flags[flagsmithFeatureFlags.CAPABILITY_DETECTION] ?? false;
  const endpointColumnEnabled =
    flags[flagsmithFeatureFlags.ENDPOINT_IDENTIFIER] ?? false;
  // KIR-3800 (AC #15/#16). Org scope only: the per-user dashboard view has a
  // single user, so a per-occurrence group column would repeat one value. The
  // group itself comes from the API (`occurrence.securityGroup`), which is
  // flag-gated server-side too.
  const securityGroupColumnEnabled =
    (flags[flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES] ?? false) &&
    !!organizationId;

  const userQuery = useUserAlertOccurrences(
    organizationId ? undefined : alertKey,
    params,
    !organizationId
  );
  const orgQuery = useOrganizationAlertOccurrences(
    organizationId ?? undefined,
    organizationId ? alertKey : undefined,
    params,
    !!organizationId
  );

  const { data, isLoading, isError, refetch } = organizationId
    ? orgQuery
    : userQuery;

  useEffect(() => {
    if (data) onDataLoaded?.(data);
  }, [data, onDataLoaded]);

  if (isLoading) {
    return <Loading label="Loading occurrences..." className="min-h-0 py-8" />;
  }

  if (isError) {
    return (
      <ErrorFallback
        retry={() => refetch()}
        label="Failed to load alert occurrences."
      />
    );
  }

  const hasOccurrences = data && data.occurrences.length > 0;

  if (!data) {
    return null;
  }

  const alert = data.alert;

  // When filters are active, the first occurrence in the breakdown table may
  // not be the actual "Base" (most recent) occurrence. Show "1" instead of
  // "Base" when the original base was filtered out.
  // Compare as epoch ms to avoid ISO-8601 formatting differences (Z vs +00:00).
  const isBaseFilteredOut =
    data.totalUnfiltered !== undefined &&
    data.totalUnfiltered > data.pagination.total &&
    hasOccurrences &&
    new Date(data.occurrences[0].scannedAt).getTime() !==
      new Date(alert.lastSeenAt).getTime();

  const tableContent = hasOccurrences ? (
    <div
      className="w-full min-w-0 rounded-md border"
      data-testid="alert-occurrences-table"
    >
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">#</TableHead>
            <TableHead>User</TableHead>
            {securityGroupColumnEnabled && (
              <TableHead className="w-[140px]">Security Group</TableHead>
            )}
            <TableHead className="w-[100px]">Last Seen</TableHead>
            <TableHead className="max-w-[180px]">File</TableHead>
            {/* min-width floors the Content column so the auto-layout table can't
                crush it to ~1ch (its inner code block uses break-all, giving a
                1-character min-content width). Floor is honored on a native td;
                the table's own overflow-x wrapper scrolls if the row overflows. */}
            <TableHead className="w-[220px] min-w-[200px]">Content</TableHead>
            {ideColumnEnabled && (
              <TableHead className="w-[140px]">IDE/Platform</TableHead>
            )}
            {endpointColumnEnabled && (
              <TableHead className="w-[140px]">Endpoint</TableHead>
            )}
            <TableHead className="w-[80px]">Severity</TableHead>
            <TableHead className="w-[100px]">Outcome</TableHead>
            <TableHead className="w-14">Score</TableHead>
            <TableHead className="w-[220px]">
              {revampEnabled ? 'Title' : 'Type'}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.occurrences.map((occ, idx) => {
            const globalIndex = (page - 1) * ALERTS_PAGE_SIZE + idx;
            const indexLabel =
              globalIndex === 0 && !isBaseFilteredOut
                ? 'Base'
                : globalIndex + 1;
            return (
              <TableRow key={`${occ.scanId}-${idx}`}>
                <TableCell className="text-sm text-muted-foreground">
                  {indexLabel}
                </TableCell>
                <TableCell className="text-sm">
                  <span className="inline-flex items-center gap-1.5">
                    {getUserDisplayName(occ.user)}
                    <UserTypeTag userType={occ.user.userType} />
                  </span>
                </TableCell>
                {securityGroupColumnEnabled && (
                  // One occurrence is one event by one user, so the API returns
                  // exactly one group — or `null` when it was produced outside
                  // any group, which reads as the organization scope.
                  //
                  // `undefined` is a THIRD state and must not collapse into
                  // "Organization": it means the field was not returned at all,
                  // which happens when the frontend flag is on but the backend
                  // one is not (same Flagsmith key, resolved through different
                  // paths, so they can disagree mid-rollout). Asserting
                  // "ungrouped" there would be a confident wrong answer, so show
                  // a dash — mirroring AlertSecurityGroupsCell.
                  <TableCell className="text-sm">
                    {occ.securityGroup === undefined ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <Badge variant="outline">
                        {occ.securityGroup
                          ? securityGroupLabel(occ.securityGroup)
                          : ORGANIZATION_SCOPE_LABEL}
                      </Badge>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-sm">
                  <RelativeTimeWithTooltip
                    date={occ.scannedAt}
                    showIcon={false}
                  />
                </TableCell>
                <TableCell className="text-sm max-w-[180px]">
                  {/* Block wrapper clamps the column's max-content contribution
                      to 180px so the width cap holds in this native
                      auto-layout table (max-width on a <td> alone is not
                      reliably honored). MiddleTruncatedPath stays w-full, so it
                      still shrinks below 180px on narrow viewports. */}
                  <div className="max-w-[180px] overflow-hidden">
                    <MiddleTruncatedPath
                      path={getAlertFileDisplayLabel({
                        filePath: occ.filePath,
                        location: occ.location,
                        eventKind: occ.eventKind,
                      })}
                      className="text-xs"
                      showInlineCopy
                    />
                  </div>
                </TableCell>
                <TableCell className="text-sm w-[220px] min-w-[200px]">
                  {occ.location ? (
                    <div className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded break-all">
                      {/* KIR-4175: drop the head-truncated description preview from
                          the location when this occurrence's evidence is shown in
                          "More details" — display-only, mirrors AlertDetailCard. */}
                      <ScanDetailFormattedText
                        location={
                          hasMcpDetectionEvidence(occ.sourceDetails)
                            ? (trimMcpToolLocationPreview(occ.location) ??
                              occ.location)
                            : occ.location
                        }
                      />
                    </div>
                  ) : (
                    '—'
                  )}
                </TableCell>
                {ideColumnEnabled && (
                  <TableCell className="text-sm">
                    <ClientTypeCell value={occ.clientType} />
                  </TableCell>
                )}
                {endpointColumnEnabled && (
                  <TableCell className="text-sm truncate max-w-[140px]">
                    {occ.endpointId ? (
                      <EndpointDetailsPopover
                        organizationId={organizationId}
                        // Endpoints are keyed on the internal users.id; occ.user.id
                        // is the Descope id after toResolvedUser (internalId holds
                        // the internal UUID). Fall back to id when unresolved.
                        userId={occ.user.internalId ?? occ.user.id}
                        machineId={occ.endpointId}
                        endpointName={occ.endpointName}
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <SeverityBadge riskLevel={occ.severity} showIcon />
                </TableCell>
                <TableCell>
                  <OccurrenceOutcomeCell row={occ} />
                </TableCell>
                <TableCell className="text-sm">{occ.score ?? '—'}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {revampEnabled
                    ? formatSubPolicyLabel(occ.subPolicyKey, occ.type)
                    : formatAlertType(occ.type)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  ) : (
    <div className="w-full min-w-0 py-4 text-center text-muted-foreground text-sm rounded-md border">
      No occurrences found.
    </div>
  );

  const pagination = (
    <SimplePagination
      page={page}
      limit={ALERTS_PAGE_SIZE}
      total={data.pagination.total}
      hasMore={data.pagination.hasMore}
      onPrevious={() => setPage(p => Math.max(1, p - 1))}
      onNext={() => setPage(p => p + 1)}
    />
  );

  if (layout === 'modal') {
    const showUserCount = !!organizationId;
    return (
      <div className="flex min-w-0 flex-col gap-4">
        {/* 1. Alert summary row — column order matches main Alerts table */}
        <div className="w-full min-w-0 rounded-md border bg-muted/20 overflow-hidden shrink-0">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Last Seen</TableHead>
                {showUserCount && (
                  <TableHead className="w-[80px] text-center">Users</TableHead>
                )}
                <TableHead className="w-[220px]">
                  {revampEnabled ? 'Title' : 'Type'}
                </TableHead>
                <TableHead className="w-[100px]">Severity</TableHead>
                <TableHead className="w-[100px]">Outcome</TableHead>
                <TableHead className="max-w-[180px]">File</TableHead>
                {ideColumnEnabled && (
                  <TableHead className="w-[160px]">IDE/Platforms</TableHead>
                )}
                <TableHead className="w-[80px] text-center">Issues</TableHead>
                <TableHead className="w-14">Score</TableHead>
                <TableHead className="w-[100px]">First Seen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>
                  <RelativeTimeWithTooltip
                    date={alert.lastSeenAt}
                    showIcon={false}
                  />
                </TableCell>
                {showUserCount && (
                  <TableCell className="text-center text-sm">
                    <UsersCell alert={alert} />
                  </TableCell>
                )}
                <TableCell>
                  <Badge variant="outline">
                    {revampEnabled
                      ? formatSubPolicyLabel(alert.subPolicyKey, alert.type)
                      : formatAlertType(alert.type)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <SeverityBadge riskLevel={alert.severity} showIcon />
                </TableCell>
                <TableCell>
                  <OutcomeBadge outcome={alert.outcome} />
                </TableCell>
                <TableCell className="max-w-[180px] text-sm">
                  {/* See the occurrence-row File cell: block wrapper clamps the
                      column's max-content contribution so the 180px cap holds
                      in this native auto-layout table. */}
                  <div className="max-w-[180px] overflow-hidden">
                    <MiddleTruncatedPath
                      path={getAlertFileDisplayLabel({
                        filePath: alert.filePath,
                        location: alert.location,
                        eventKind: alert.eventKind,
                      })}
                      className="text-xs"
                      showInlineCopy
                    />
                  </div>
                </TableCell>
                {ideColumnEnabled && (
                  <TableCell>
                    <ClientTypesCell row={alert} />
                  </TableCell>
                )}
                <TableCell className="text-center text-sm">
                  {alert.occurrenceCount ?? '—'}
                </TableCell>
                <TableCell className="text-sm">{alert.score ?? '—'}</TableCell>
                <TableCell>
                  <RelativeTimeWithTooltip
                    date={alert.firstSeenAt}
                    showIcon={false}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* 2. Finding detail */}
        <section className="w-full min-w-0 shrink-0">
          {capabilityDetectionEnabled && (
            <h2 className="mb-2 text-sm font-medium">Findings</h2>
          )}
          <AlertDetailCard
            alert={alert}
            className="w-full min-w-0"
            organizationId={organizationId}
            scope={
              organizationId
                ? { kind: 'organization', organizationId }
                : { kind: 'account' }
            }
            alertKey={alertKey}
          />
        </section>

        {/* 2b. Capability summary (extension-security scans only) */}
        {capabilityDetectionEnabled &&
          data.capabilities &&
          data.capabilities.length > 0 && (
            <section>
              <h2 className="mb-2 text-sm font-medium">Capabilities</h2>
              <CapabilitySummary capabilities={data.capabilities} />
            </section>
          )}

        {/* 3. Occurrences table + pagination */}
        <div className="min-w-0">
          {tableContent}
          <div className="pt-2">{pagination}</div>
        </div>
      </div>
    );
  }

  if (!hasOccurrences) {
    return (
      <div className="py-4 text-center text-muted-foreground text-sm">
        No occurrences found.
      </div>
    );
  }

  return (
    <div className="mt-2">
      {tableContent}
      {capabilityDetectionEnabled &&
        data.capabilities &&
        data.capabilities.length > 0 && (
          <CapabilitySummary capabilities={data.capabilities} />
        )}
      {pagination}
    </div>
  );
}
